import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { Bell, GraduationCap, ShieldCheck, Trash2, type LucideIcon } from '../components/icons';
import { IconPill } from '../components/IconPill';
import { UiLanguagePicker } from '../components/UiLanguagePicker';
import { useGabarit } from '../hooks/useGabarit';
import { t } from '../i18n';
import { fleche } from '../i18n/sens';
import type { AccentKey } from '../theme/accents';
import { LARGEUR_EN_RANGEE } from '../theme/miseEnPage';
import { colors, layout, radius, shadow, spacing, typography } from '../theme/tokens';

export interface ParametresLargeProps {
  onOpenPrivacy: () => void;
  onOpenDataDeletion: () => void;
  onOpenNotifications: () => void;
  onSignOut: () => void;
  onDeleteAccount: () => void;
  deleting: boolean;
  /** Absents quand le parcours n'est pas branché (licence école) ou que la personne n'est pas admin. */
  onOpenLicenceEcole?: () => void;
  onOpenAdmin?: () => void;
}

const TRANSITION = { transitionProperty: 'background-color', transitionDuration: '120ms' } as object;

interface LigneProps {
  label: string;
  onPress: () => void;
  testID: string;
  icon?: LucideIcon;
  accent?: AccentKey;
  danger?: boolean;
}

/**
 * Ligne d'une carte de réglages : pastille d'icône, libellé, chevron. Au survol le fond se teinte,
 * ce qu'un pouce n'a pas besoin de montrer mais qu'une souris attend. Les lignes dangereuses n'ont
 * ni pastille ni chevron : le rouge du texte dit déjà ce qu'elles font.
 */
function Ligne({ label, onPress, testID, icon, accent = 'blue', danger = false }: LigneProps) {
  const [survol, setSurvol] = useState(false);
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      onPress={onPress}
      onHoverIn={() => setSurvol(true)}
      onHoverOut={() => setSurvol(false)}
      style={[styles.ligne, survol && styles.ligneSurvol, TRANSITION]}
    >
      <View style={styles.ligneGauche}>
        {icon ? <IconPill icon={icon} accent={accent} variant="soft" size={36} /> : null}
        <Text style={[styles.ligneLibelle, danger && styles.ligneDanger]}>{label}</Text>
      </View>
      {danger ? null : <Text style={styles.chevron}>{fleche('›')}</Text>}
    </Pressable>
  );
}

/** Carte d'une famille de réglages : filet de couleur en haut, titre, puis ses lignes. */
function Carte({ titre, couleur, testID, children }: { titre: string; couleur: string; testID: string; children: React.ReactNode }) {
  return (
    <View testID={testID} style={styles.carte}>
      <View style={[styles.filet, { backgroundColor: couleur }]} />
      <Text style={styles.carteTitre}>{titre}</Text>
      <View style={styles.lignes}>{children}</View>
    </View>
  );
}

/**
 * Paramètres sur ordinateur et tablette : des cartes par famille (compte, préférences, zone de
 * danger, administration) au lieu d'une pile de pilules. Deux colonnes sur grand écran, une seule
 * sur tablette. Les textes et les identifiants de test sont ceux du téléphone.
 */
export function ParametresLarge(p: ParametresLargeProps) {
  const { etendu: gabaritEtendu, largeur } = useGabarit();
  const enColonnes = gabaritEtendu && largeur >= LARGEUR_EN_RANGEE;

  const compte = (
    <Carte titre={t('settings.sections.account')} couleur={colors.electric} testID="settings-carte-compte">
      {p.onOpenLicenceEcole ? (
        <Ligne icon={GraduationCap} label={t('settings.licenceEcole')} onPress={p.onOpenLicenceEcole} testID="settings-licence-ecole" />
      ) : null}
      <Ligne icon={ShieldCheck} label={t('settings.privacy')} onPress={p.onOpenPrivacy} testID="settings-privacy" />
      <Ligne icon={Trash2} label={t('settings.dataDeletion')} onPress={p.onOpenDataDeletion} testID="settings-data-deletion" />
      <Ligne danger label={t('settings.signOut')} onPress={p.onSignOut} testID="account-signout" />
    </Carte>
  );
  const preferences = (
    <Carte titre={t('settings.sections.preferences')} couleur={colors.violet} testID="settings-carte-preferences">
      <Ligne icon={Bell} label={t('settings.notifications')} onPress={p.onOpenNotifications} testID="settings-notifications" />
      <UiLanguagePicker />
    </Carte>
  );
  const admin = p.onOpenAdmin ? (
    <Carte titre={t('settings.sections.admin')} couleur={colors.cyan} testID="settings-carte-admin">
      <Ligne icon={ShieldCheck} accent="lime" label={t('settings.adminDashboard')} onPress={p.onOpenAdmin} testID="settings-admin" />
    </Carte>
  ) : null;
  const danger = (
    <Carte titre={t('settings.sections.danger')} couleur={colors.error} testID="settings-carte-danger">
      <Ligne
        danger
        label={p.deleting ? t('settings.deleting') : t('settings.deleteAccount')}
        onPress={p.deleting ? () => {} : p.onDeleteAccount}
        testID="account-delete"
      />
    </Carte>
  );

  return (
    <View style={styles.container}>
      <ScrollView
        testID="settings-page-large"
        contentContainerStyle={[styles.content, !enColonnes && styles.contentMoyen]}
        showsVerticalScrollIndicator={false}
      >
        {enColonnes ? (
          <View testID="settings-colonnes" style={[styles.page, styles.deuxColonnes]}>
            <View style={styles.colonne}>
              {compte}
              {danger}
            </View>
            <View style={styles.colonne}>
              {preferences}
              {admin}
            </View>
          </View>
        ) : (
          <View testID="settings-colonnes" style={styles.page}>
            {compte}
            {preferences}
            {admin}
            {danger}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: {
    flexGrow: 1,
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.xxl + spacing.xl,
  },
  contentMoyen: { paddingHorizontal: spacing.xxl },
  page: { width: '100%', maxWidth: layout.pageMaxWidth, alignSelf: 'center', gap: spacing.lg },
  deuxColonnes: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.lg },
  colonne: { flex: 1, minWidth: 0, gap: spacing.lg },

  carte: {
    paddingTop: spacing.xl + spacing.sm,
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    overflow: 'hidden',
    ...shadow.card,
  },
  filet: { position: 'absolute', top: 0, left: 0, right: 0, height: 5 },
  carteTitre: {
    fontSize: typography.h1.size - 2,
    lineHeight: typography.h1.lineHeight,
    fontWeight: '800',
    color: colors.navy,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  lignes: { gap: spacing.xs },

  ligne: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 56,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.lg,
    cursor: 'pointer',
  },
  ligneSurvol: { backgroundColor: colors.grey50 },
  ligneGauche: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexShrink: 1 },
  ligneLibelle: { fontSize: typography.bodyMd.size, color: colors.navy },
  ligneDanger: { color: colors.error, fontWeight: typography.bodyMd.weight },
  chevron: { fontSize: typography.h3.size, color: colors.grey400 },
});
